import { Inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, fromEvent, Subscription } from "rxjs";
import { filter, map, take } from "rxjs/operators";
import { SecoPilotingConfig } from "../../seco-piloting-config";

/**
 * A Service to ensure that a Seco Window is open.
 */
@Injectable({providedIn: 'root'})
export class SecoWindowService implements OnDestroy {

  private win: any = null;
  private linkSubscription: Subscription = null;

  public windowIsOpen$ = new BehaviorSubject(false);
  readonly SECO_CONFIGURED_LANGUAGES = ['BR', 'CN', 'DE', 'ES', 'FR', 'GB', 'IT', 'JP', 'KO', 'RU', 'TW', 'US'];
  readonly  DEFAULT_LANGUAGE = 'GB';

  constructor(private config: SecoPilotingConfig,
              @Inject('SMARTSCRIPT_WINDOW') private window: any) {

    if (!this.window.location.origin.startsWith('https')) {
      console.warn('[seco-piloting] Smart scripting requires serving using https.');
    }

  }

  init() {
    const findLink = (el: HTMLElement) => {
      while (el) {
        if((el.nodeName || el.tagName).toLowerCase() === 'a') {
          return el;
        }
        el = el.parentNode as HTMLElement;
      }
      return null;
    };

    return this.config.loginUrl$
      .then((url: URL) => {
        return fromEvent(window.document.body, 'click').pipe(
          map((event: MouseEvent) => [event, findLink(event.target as HTMLElement)]),
          filter(([,element]: [MouseEvent, HTMLAnchorElement]) => {
            return !!element && !!element.href && !!url
          }),
          map(([event,element]) => [event, new URL(element.href)]),
          filter(([,targetUrl]: [MouseEvent, URL]) => targetUrl.searchParams.has('redirect_uri')
            && url.searchParams.has('redirect_uri')
          ),
          filter(([,targetUrl]: [MouseEvent, URL]) => {
            const target = new URL(targetUrl.searchParams.get('redirect_uri'));
            const secoConfig = new URL(url.searchParams.get('redirect_uri'));
            return secoConfig.origin === target.origin;
          })
        ).subscribe(([event]) => {
          event.preventDefault();
          this.openOrFocus();
        });
      })
      .then((subscription: Subscription) => this.linkSubscription = subscription);
  }

  ngOnDestroy(): void {
    if (this.linkSubscription){
      this.linkSubscription.unsubscribe();
    }
  }

  /**
   * Focus an already opened window or open a new one.
   */
  openOrFocus() {
    if (!this.win || this.win.closed){
      if(this.win && this.win.closed){
        this.windowIsOpen$.next(false);
      }
      return this.open().then((win) => {
        this.win = win;
        this.windowIsOpen$.next(true);
        return this.win;
      });
    } else {
      this.win.focus();
      return Promise.resolve(this.win);
    }
  }

  /**
   * Open a new window.
   */
  open(): Promise<Window> {
    return this.config.loginUrl$
      .then((url: URL) => {
        return this.config.language$.pipe(take(1)).toPromise()
          .then((lang: string) => {
            if(url.searchParams.has('redirect_uri')){
              const targetUrl = new URL(url.searchParams.get('redirect_uri'));
              if (this.SECO_CONFIGURED_LANGUAGES.includes(lang)) {
                targetUrl.searchParams.set('LANGUAGE', lang);
              } 
              else {
                targetUrl.searchParams.set('LANGUAGE',this.DEFAULT_LANGUAGE);
              }
              if (targetUrl.searchParams.has('OV_SITE_SMART_PORTAL_URL')) {
                targetUrl.searchParams.set('OV_SITE_SMART_PORTAL_URL', this.window.location.origin);
              }
              url.searchParams.set('redirect_uri', targetUrl.href);
            }
            return this.window.open(url.href);
          })
      });
  }
}
